/**
 * NotificationContext - Context untuk menyimpan dan mengelola state notifikasi
 * Mengurangi duplikasi fetch dengan menyimpan state di level context
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { API_ESA_OFFICE, getAuthHeaders } from "utils";

/** Rute publik yang tidak memerlukan fetch notifikasi (tanpa auth) */
const isPublicRoute = (pathname) =>
  pathname.startsWith("/survey/") ||
  pathname.startsWith("/publik/survey-kepuasan/") ||
  pathname.startsWith("/publik/buku-tamu/") ||
  pathname.startsWith("/publik/events/") ||
  pathname.startsWith("/download/rote-smart-service");

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within NotificationProvider");
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { pathname } = useLocation();
  const [notifications, setNotifications] = useState({
    instruksi: {
      instruksi_baru: 0,
      instruksi_selesai_belum_respon: 0,
    },
    disposisi: {
      surat_masuk_belum_disposisi: 0,
      disposisi_baru: 0,
      disposisi_selesai_belum_respon: 0,
    },
  });
  const [loading, setLoading] = useState(true);
  const [lastFetch, setLastFetch] = useState(null);

  /**
   * Fetch semua notifikasi dari endpoint gabungan
   * Tidak dijalankan saat di rute publik (survey, buku tamu, daftar hadir event, dll)
   */
  const fetchNotifications = useCallback(async () => {
    if (isPublicRoute(pathname)) {
      setLoading(false);
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user || !user.uid) {
        setLoading(false);
        return;
      }

      const url = `${API_ESA_OFFICE}instruksi/notifikasi/all/${user.uid}`;
      const response = await fetch(url, {
        credentials: "include",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      setNotifications({
        instruksi: {
          instruksi_baru: data.instruksi?.instruksi_baru || 0,
          instruksi_selesai_belum_respon:
            data.instruksi?.instruksi_selesai_belum_respon || 0,
        },
        disposisi: {
          surat_masuk_belum_disposisi:
            data.disposisi?.surat_masuk_belum_disposisi || 0,
          disposisi_baru: data.disposisi?.disposisi_baru || 0,
          disposisi_selesai_belum_respon:
            data.disposisi?.disposisi_selesai_belum_respon || 0,
        },
      });

      setLastFetch(new Date());
    } catch (error) {
      console.error("Error fetching notifications:", error);
      // Tetap set loading false meskipun error
    } finally {
      setLoading(false);
    }
  }, [pathname]);

  /**
   * Refresh notifications secara manual
   */
  const refreshNotifications = useCallback(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    // Fetch pertama kali saat mount
    fetchNotifications();

    // Auto-refresh setiap 30 menit (1800000 ms)
    const interval = setInterval(fetchNotifications, 1800000);

    // Refresh saat window focus
    const handleFocus = () => {
      fetchNotifications();
    };
    window.addEventListener("focus", handleFocus);

    // Listen untuk custom events untuk refresh real-time
    const handleInstruksiDiterima = () => {
      fetchNotifications();
    };
    const handleInstruksiDirespon = () => {
      fetchNotifications();
    };
    const handleSuratMasukDidisposisi = () => {
      fetchNotifications();
    };
    const handleDisposisiDibaca = () => {
      fetchNotifications();
    };
    const handleDisposisiDirespon = () => {
      fetchNotifications();
    };

    window.addEventListener("instruksiDiterima", handleInstruksiDiterima);
    window.addEventListener("instruksiDirespon", handleInstruksiDirespon);
    window.addEventListener(
      "suratMasukDidisposisi",
      handleSuratMasukDidisposisi
    );
    window.addEventListener("disposisiDibaca", handleDisposisiDibaca);
    window.addEventListener("disposisiDirespon", handleDisposisiDirespon);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("instruksiDiterima", handleInstruksiDiterima);
      window.removeEventListener("instruksiDirespon", handleInstruksiDirespon);
      window.removeEventListener(
        "suratMasukDidisposisi",
        handleSuratMasukDidisposisi
      );
      window.removeEventListener("disposisiDibaca", handleDisposisiDibaca);
      window.removeEventListener("disposisiDirespon", handleDisposisiDirespon);
    };
  }, [fetchNotifications]);

  const value = {
    notifications,
    loading,
    lastFetch,
    refreshNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
